import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { obtenerCotizacion } from "../services/cotizacionService";
import { obtenerEmpresa } from "../services/empresaService";
import { numeroALetras } from "../utils/numeroALetras";
import { getLogoUrl, getEmpresaId } from "../utils/logoUtils";
import "./FacturaPrint.css";

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const calcPrecioVenta = (precioBase, aum = 0) => {
  const base = num(precioBase);
  const pAum = num(aum);
  if (pAum > 0 && pAum < 100) return round2(base / (1 - pAum / 100));
  return round2(base);
};

const formatDate = (d) => {
  if (!d) return "";
  try {
    const dt = new Date(d);
    if (isNaN(dt)) return String(d);
    return dt.toLocaleDateString("es-MX", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch {
    return String(d);
  }
};

const mapEmpresa = (e) => {
  const defaults = { nombre: "Mi Empresa", direccion: "", rfc: "", telefono: "" };
  if (!e || typeof e !== "object") return defaults;
  const n = Object.fromEntries(
    Object.entries(e).map(([k, v]) => [String(k).toLowerCase(), v]),
  );
  const pick = (...keys) => {
    const found = keys.map((k) => k.toLowerCase()).find((k) => n[k] != null);
    return found ? n[found] : undefined;
  };
  const parts = [
    pick("calle_empresa", "calle"),
    pick("num_ext_empresa", "numext", "num_ext") ? `#${pick("num_ext_empresa", "numext", "num_ext")}` : "",
    pick("num_int_empresa", "numint", "num_int") ? `Int. ${pick("num_int_empresa", "numint", "num_int")}` : "",
    pick("colonia_empresa", "colonia"),
    pick("cp_empresa", "cp", "codigo_postal") ? `CP ${pick("cp_empresa", "cp", "codigo_postal")}` : "",
    pick("ciudad_empresa", "ciudad", "municipio"),
    pick("estado_empresa", "estado"),
    pick("pais"),
  ].filter(Boolean);
  return {
    nombre: pick("nombre_empresa", "nombre", "razon_social", "razonsocial") || "Mi Empresa",
    direccion: parts.join(" · "),
    rfc: pick("rfc_empresa", "rfc") || "",
    telefono: pick("telefonos_empresa", "telefono", "telefono1") || "",
  };
};

export default function CotizacionPrint40() {
  const { id } = useParams();
  const [cotData, setCotData] = useState(null);
  const [empresa, setEmpresa] = useState(mapEmpresa(null));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await obtenerCotizacion(id);
        if (!cancel) setCotData(data || null);
      } catch {
        if (!cancel) setError("No se pudo cargar la cotización para impresión.");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [id]);

  useEffect(() => {
    const fromLS = JSON.parse(localStorage.getItem("empresa") || "null");
    if (fromLS) { setEmpresa(mapEmpresa(fromLS)); return; }
    const empId = Number(localStorage.getItem("empresaId") || 0);
    if (!empId) return;
    (async () => {
      try {
        const resp = await obtenerEmpresa(empId);
        const raw = resp?.data?.data ?? resp?.data ?? resp ?? null;
        const obj = Array.isArray(raw) ? raw[0] : raw;
        if (obj) setEmpresa(mapEmpresa(obj));
      } catch {}
    })();
  }, []);

  useEffect(() => {
    if (!loading && cotData) {
      const timer = window.setTimeout(() => window.print(), 500);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [loading, cotData]);

  const computed = useMemo(() => {
    if (!cotData) return null;

    const partidas = (cotData.cotizacionesDetalle || []).map((d, i) => {
      const precioBase = num(d.precioCompra ?? d.precio_compra ?? 0);
      const pAum = num(d.porcIncremento ?? d.porc_incremento ?? 0);
      const pDesc = num(d.porcDescto ?? d.porcdescto ?? 0);
      const cantidad = num(d.cantidad || 0);
      const pVenta = calcPrecioVenta(precioBase, pAum);
      const pUnitDesc = pDesc > 0 ? round2(pVenta * (1 - pDesc / 100)) : pVenta;
      const importe = round2(cantidad * pUnitDesc);
      return {
        partida: i + 1,
        clave: d.articulo || "",
        descripcion: d.descripcion || "",
        um: d.unidadmedida || d.unidadMedida || "",
        cantidad,
        pUnitario: pVenta,
        desc: pDesc,
        importe,
      };
    });

    let subtotal = round2(partidas.reduce((s, r) => s + r.importe, 0));
    const pDscto = num(cotData.porcDscto || 0);
    const pIncGlobal = num(cotData.incrementoGlobal || 0);
    if (pDscto > 0) subtotal = round2(subtotal * (1 - pDscto / 100));
    if (pIncGlobal > 0) subtotal = round2(subtotal * (1 + pIncGlobal / 100));
    const iva = round2(subtotal * 0.16);
    const total = round2(subtotal + iva);
    const moneda = cotData.moneda === "D" ? "USD" : "MXN";
    const leyenda = numeroALetras(total, { currency: moneda });

    return { partidas, subtotal, iva, total, moneda, leyenda };
  }, [cotData]);

  if (loading) return <div className="print-page-pad">Cargando cotización…</div>;
  if (error) return <div className="print-page-pad print-error">{error}</div>;
  if (!cotData || !computed) return <div className="print-page-pad">No se encontró la cotización.</div>;

  const { partidas, subtotal, iva, total, moneda, leyenda } = computed;
  const folio = cotData.cotizacion ?? id;
  const fmt2 = (n) => n.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const S = {
    accentBar:   { height: 4, background: "#1a3e72", borderRadius: 2, margin: "7px 0 6px" },
    infoBar:     { display: "grid", gridTemplateColumns: "auto auto 1fr auto", gap: 0, border: "1px solid #c9d4e8", borderRadius: 6, overflow: "hidden", marginBottom: 10 },
    infoCell:    { padding: "7px 14px", borderRight: "1px solid #c9d4e8" },
    infoCellLast:{ padding: "7px 14px" },
    infoLabel:   { fontSize: 9, color: "#6b7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 2 },
    infoValue:   { fontSize: 12, fontWeight: 700, color: "#1a1a2e" },
    folioValue:  { fontSize: 18, fontWeight: 800, color: "#1a3e72" },
    th:          { color: "#fff", background: "#1a3e72", padding: "7px 8px", fontSize: 11, fontWeight: 700, border: "none" },
    thNum:       { color: "#fff", background: "#1a3e72", padding: "7px 8px", fontSize: 11, fontWeight: 700, border: "none", textAlign: "right" },
    td:          { padding: "6px 8px", borderBottom: "1px solid #eef0f5", fontSize: 12, verticalAlign: "top" },
    tdNum:       { padding: "6px 8px", borderBottom: "1px solid #eef0f5", fontSize: 12, verticalAlign: "top", textAlign: "right", fontVariantNumeric: "tabular-nums" },
    totalsWrap:  { display: "grid", gridTemplateColumns: "1fr 230px", border: "1px solid #c9d4e8", borderTop: "none", marginBottom: 10, pageBreakInside: "avoid", breakInside: "avoid" },
    letraBox:    { padding: "9px 12px", borderRight: "1px solid #c9d4e8" },
    letraLabel:  { fontSize: 9, color: "#6b7280", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 3 },
    letraText:   { fontSize: 11, lineHeight: 1.45, color: "#222" },
    totalsBox:   { padding: "8px 12px", display: "flex", flexDirection: "column", justifyContent: "center" },
    totalRow:    { display: "flex", justifyContent: "space-between", fontSize: 12, padding: "2px 0", borderBottom: "1px solid #eef0f5", color: "#444" },
    totalFinal:  { display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 800, padding: "7px 8px", marginTop: 4, background: "#1a3e72", color: "#fff", borderRadius: 4 },
    noteBox:     { border: "1px solid #e2e8f0", borderRadius: 4, padding: "8px 10px" },
    noteLabel:   { fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "#6b7280", letterSpacing: 0.6, marginBottom: 4 },
    noteText:    { fontSize: 11, whiteSpace: "pre-wrap", lineHeight: 1.5, color: "#333" },
  };

  return (
    <div className="print-page">
      <main className="print-doc">

        {/* ── ENCABEZADO ─────────────────────────────────────── */}
        <div className="print-header">
          <img src={getLogoUrl(getEmpresaId())} alt="Logo" className="print-logo" onError={(e) => { e.target.style.display = "none"; }} />
          <div className="print-header-center">
            <div className="print-empresa-nombre">{empresa.nombre}</div>
            {empresa.direccion && (
              <div style={{ fontSize: 10, color: "#555", marginTop: 2 }}>
                {empresa.direccion}
                {empresa.rfc && <span>  ·  RFC: {empresa.rfc}</span>}
                {empresa.telefono && <span>  ·  Tel: {empresa.telefono}</span>}
              </div>
            )}
            <div style={{
              display: "inline-block", marginTop: 6,
              padding: "4px 22px", border: "2px solid #1a3e72",
              borderRadius: 3, fontSize: 17, fontWeight: 800,
              color: "#1a3e72", letterSpacing: 3,
            }}>
              COTIZACIÓN
            </div>
          </div>
          <img src={getLogoUrl(getEmpresaId(), "secundario")} alt="Logo secundario" className="print-logo print-logo-right" onError={(e) => { e.target.style.display = "none"; }} />
        </div>

        {/* Barra de acento */}
        <div style={S.accentBar} />

        {/* ── BARRA DE INFO: Folio · Fecha · Cliente · Moneda ── */}
        <div style={S.infoBar}>
          <div style={{ ...S.infoCell, background: "#f0f4ff" }}>
            <div style={S.infoLabel}>Folio</div>
            <div style={S.folioValue}>{folio}</div>
          </div>
          <div style={S.infoCell}>
            <div style={S.infoLabel}>Fecha</div>
            <div style={S.infoValue}>{formatDate(cotData.fecha)}</div>
          </div>
          <div style={S.infoCell}>
            <div style={S.infoLabel}>Cliente</div>
            <div style={S.infoValue}>{cotData.nombreCliente || cotData.cliente}</div>
          </div>
          <div style={S.infoCellLast}>
            <div style={S.infoLabel}>Moneda</div>
            <div style={S.infoValue}>{moneda}</div>
          </div>
        </div>

        {/* ── TABLA DE CONCEPTOS ─────────────────────────────── */}
        <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #c9d4e8" }}>
          <colgroup>
            <col style={{ width: "4%" }} />
            <col style={{ width: "10%" }} />
            <col />
            <col style={{ width: "6%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "12%" }} />
            <col style={{ width: "7%" }} />
            <col style={{ width: "12%" }} />
          </colgroup>
          <thead>
            <tr>
              {[
                ["#",           false],
                ["Artículo",    false],
                ["Descripción", false],
                ["UM",          false],
                ["Cantidad",    true],
                ["P. Unitario", true],
                ["% Desc.",     true],
                ["Importe",     true],
              ].map(([h, right]) => (
                <th key={h} style={right ? S.thNum : S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {partidas.map((r, i) => (
              <tr key={r.partida} style={{ background: i % 2 === 0 ? "#fff" : "#f6f8ff" }}>
                <td style={{ ...S.td, textAlign: "center", color: "#6b7280" }}>{r.partida}</td>
                <td style={{ ...S.td, fontWeight: 600 }}>{r.clave}</td>
                <td style={{ ...S.td, wordBreak: "break-word" }}>{r.descripcion}</td>
                <td style={{ ...S.td, textAlign: "center" }}>{r.um}</td>
                <td style={S.tdNum}>{fmt2(r.cantidad)}</td>
                <td style={S.tdNum}>{fmt2(r.pUnitario)}</td>
                <td style={{ ...S.tdNum, color: r.desc > 0 ? "#b45309" : "#6b7280" }}>{fmt2(r.desc)}</td>
                <td style={{ ...S.tdNum, fontWeight: 700 }}>{fmt2(r.importe)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* ── TOTALES ────────────────────────────────────────── */}
        <div style={S.totalsWrap}>
          <div style={S.letraBox}>
            <div style={S.letraLabel}>Importe con letra</div>
            <div style={S.letraText}>{leyenda}</div>
          </div>
          <div style={S.totalsBox}>
            <div style={S.totalRow}>
              <span>Subtotal</span><span>{fmt2(subtotal)}</span>
            </div>
            <div style={S.totalRow}>
              <span>IVA 16%</span><span>{fmt2(iva)}</span>
            </div>
            <div style={S.totalFinal}>
              <span>Total {moneda}</span><span>{fmt2(total)}</span>
            </div>
          </div>
        </div>

        {/* ── CONDICIONES / NOTAS ────────────────────────────── */}
        {(cotData.observaciones || cotData.notasAdicionales) && (
          <div style={{
            display: "grid",
            gridTemplateColumns: cotData.observaciones && cotData.notasAdicionales ? "1fr 1fr" : "1fr",
            gap: 8, marginBottom: 10,
          }}>
            {cotData.observaciones && (
              <div style={S.noteBox}>
                <div style={S.noteLabel}>Condiciones</div>
                <div style={S.noteText}>{cotData.observaciones}</div>
              </div>
            )}
            {cotData.notasAdicionales && (
              <div style={S.noteBox}>
                <div style={S.noteLabel}>Notas Adicionales</div>
                <div style={S.noteText}>{cotData.notasAdicionales}</div>
              </div>
            )}
          </div>
        )}

        {/* ── PIE DE PÁGINA ──────────────────────────────────── */}
        <div style={{
          borderTop: "2px solid #1a3e72", paddingTop: 7, marginTop: 6,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div style={{ fontSize: 10, color: "#555", maxWidth: "75%" }}>
            Esta cotización es válida por 30 días a partir de la fecha de emisión
            o hasta agotar existencias, lo que ocurra primero.
          </div>
          <div style={{ fontSize: 10, color: "#1a3e72", fontWeight: 700, textAlign: "right" }}>
            {empresa.nombre}
          </div>
        </div>

      </main>
    </div>
  );
}
